import axios, { AxiosError } from "axios";
import { useCallback, useContext, useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import { unstable_serialize, useSWRConfig } from "swr";
import {
  GenericErrorResponse,
  GetNotifications200Response,
  NotificationTypeResponse,
} from "../../../codegen-api";
import { AuthContext } from "../../../contexts/AuthContext";
import { APIEndpointEnum } from "../../../enums/endpoint";
import { authApi } from "../../../services/api/apiFetcher";
import { API_URL } from "../../../utils/env";
import { getOnchainTokenDecimals } from "../../../config/tokenDecimals";
import { getNotificationDedupeKey } from "../../../utils/notifications.helpers";
import { useLoggerMiddleware } from "../middleware/useLoggerMiddleware";

export const supportedNotificationTypes: NotificationTypeResponse[] = [
  NotificationTypeResponse.Fill,
  NotificationTypeResponse.LiquidationStarted,
  NotificationTypeResponse.LiquidationCompleted,
  NotificationTypeResponse.DepositFinalized,
  NotificationTypeResponse.WithdrawalFinalized,
  NotificationTypeResponse.MmpUpdated,
  NotificationTypeResponse.MmpTriggered,
  NotificationTypeResponse.MmpUnfrozen,
  NotificationTypeResponse.Receive,
  NotificationTypeResponse.Settled,
  NotificationTypeResponse.EmailVerification,
  NotificationTypeResponse.SendFinalized,
  NotificationTypeResponse.FarmBoostApplied,
  // Referral program is a live feature — surface its notifications instead of
  // silently dropping them in the WSS cache filter / unread badge.
  NotificationTypeResponse.Referral,
  NotificationTypeResponse.Referee,
  // TP/SL trigger outcomes from the off-chain executor (POST /tpsl/webhook).
  NotificationTypeResponse.TpslExecuted,
  NotificationTypeResponse.TpslRejected,
];

// Notification types that are considered "alerts"
export const alertNotificationTypes: NotificationTypeResponse[] = [
  NotificationTypeResponse.MmpTriggered,
  NotificationTypeResponse.LiquidationStarted,
  NotificationTypeResponse.LiquidationCompleted,
  // A trigger that FAILED to execute leaves the position open with the
  // protection the trader thought they had — that's an alert, not a trade.
  NotificationTypeResponse.TpslRejected,
];

const ACTIVE_NOTIFICATION_CHAIN_ID = String(process.env.REACT_APP_CHAIN_ID || "");

type ChainTaggedNotification = GetNotifications200Response & {
  chain_id?: string | number;
  chainId?: string | number;
};

export const notificationSWRKey = (apiKey?: string) =>
  apiKey
    ? [APIEndpointEnum.NOTIFICATIONS, apiKey, ACTIVE_NOTIFICATION_CHAIN_ID]
    : undefined;

export const isNotificationForActiveChain = (
  notification: GetNotifications200Response
) => {
  const chainId =
    (notification as ChainTaggedNotification).chain_id ??
    (notification as ChainTaggedNotification).chainId;

  return (
    !chainId ||
    !ACTIVE_NOTIFICATION_CHAIN_ID ||
    String(chainId) === ACTIVE_NOTIFICATION_CHAIN_ID
  );
};

function hasCounterparty(notification: GetNotifications200Response) {
  return Boolean(String(notification.metadata?.account || "").trim());
}

function isExplicitVaultSend(notification: GetNotifications200Response) {
  const label = notification.metadata?.label;
  return label === "YV_WITHDRAW" || label === "YV_REDEEM" || label === "YV_DEPOSIT";
}

// Deposit/withdraw/transfer types carry a token amount + collateral address.
const transferAmountTypes: NotificationTypeResponse[] = [
  NotificationTypeResponse.DepositFinalized,
  NotificationTypeResponse.WithdrawalFinalized,
  NotificationTypeResponse.Receive,
  NotificationTypeResponse.SendFinalized,
];

export const isDisplayableNotification = (
  notification: GetNotifications200Response
) => {
  if (!isNotificationForActiveChain(notification)) return false;
  if (!supportedNotificationTypes.includes(notification.notification_type)) return false;

  // A transfer notification whose collateral token is not a known on-chain token
  // of the ACTIVE chain can't have its amount displayed (no fallback decimals —
  // a 6-dec amount scaled by a guessed 18 renders "0.00"). These are stale /
  // cross-chain rows (e.g. chain_id=0 leakage); hide them rather than guess.
  if (transferAmountTypes.includes(notification.notification_type)) {
    const collateral = notification.metadata?.collateral;
    if (collateral && getOnchainTokenDecimals(collateral) === undefined) {
      return false;
    }
  }

  if (notification.notification_type === NotificationTypeResponse.SendFinalized) {
    return hasCounterparty(notification) || isExplicitVaultSend(notification);
  }

  if (notification.notification_type === NotificationTypeResponse.Receive) {
    return hasCounterparty(notification) || Boolean(notification.metadata?.label);
  }

  return true;
};

function authHeaders(apiKey?: string, apiSecret?: string) {
  return apiKey && apiSecret
    ? { "API-KEY": apiKey, "API-SECRET": apiSecret }
    : undefined;
}

const toBigTs = (s?: string) => {
  try {
    return BigInt(s || "0");
  } catch {
    return 0n;
  }
};

// Merge the authoritative server list with whatever is already in the cache.
// The cache may hold notifications delivered live over the websocket that the
// server fetch hasn't returned yet (it arrived after the REST query ran, or the
// row is newer than the server's 100-row window). A blind replace would drop
// those — making a just-arrived notification "appear then disappear" the moment
// a refetch lands. The server version wins for any id it returns (authoritative
// for is_read / removals); current-only items (newest live deltas) are kept.
//
// Identity is the STABLE content key (type + tx_hash/fill_id, falling back to
// id), not the volatile UUID — the backend catch-up poller re-emits the same
// on-chain event with a fresh id, so an id-keyed merge would keep both the
// cached copy and the re-emitted one and pile up duplicates. Same key the live
// WSS path dedupes on.
export function mergeNotifications(
  current: GetNotifications200Response[],
  fresh: GetNotifications200Response[]
): GetNotifications200Response[] {
  const freshKeys = new Set(fresh.map(getNotificationDedupeKey));
  const extras = current.filter(
    (c) => !freshKeys.has(getNotificationDedupeKey(c))
  );
  const merged = [...fresh, ...extras];
  merged.sort((a, b) => {
    const at = toBigTs(a.created_timestamp);
    const bt = toBigTs(b.created_timestamp);
    return at > bt ? -1 : at < bt ? 1 : 0;
  });
  return merged.slice(0, 100);
}

export const useNotifications = () => {
  const { apiKey, apiSecret } = useContext(AuthContext);
  const { cache } = useSWRConfig();
  const fetcher = useMemo(
    () => authApi(apiKey, apiSecret),
    [apiKey, apiSecret]
  );
  const HIGH_LOAD_MODE = process.env.REACT_APP_HIGH_LOAD_MODE === "true";

  const swrKey = !HIGH_LOAD_MODE ? notificationSWRKey(apiKey) : undefined;
  const serializedKey = swrKey ? unstable_serialize(swrKey) : undefined;

  const swr = useSWRImmutable<GetNotifications200Response[], AxiosError>(
    swrKey,
    async () => {
      // Snapshot whatever is already cached (includes live WSS deltas) so we can
      // merge rather than clobber, and so a transient fetch error never wipes the
      // list out from under the user.
      const current =
        ((serializedKey
          ? (cache.get(serializedKey)?.data as
              | GetNotifications200Response[]
              | undefined)
          : undefined) as GetNotifications200Response[] | undefined) ?? [];
      try {
        if (!HIGH_LOAD_MODE) {
          const { data } = await (await fetcher.getNotifications(undefined, 100))();
          const fresh = data.filter(isDisplayableNotification);
          return mergeNotifications(current, fresh);
        }

        return current;
      } catch (error) {
        // Keep what we have — do NOT replace the cache with [] on a transient
        // failure (auth window, network blip), which previously made live
        // notifications appear and then vanish on the next refetch.
        return current;
      }
    },
    {
      use: [useLoggerMiddleware],
      revalidateOnMount: false,
    }
  );

  const markAsRead = useCallback(async () => {
    try {
      // Functional updater (not a captured swr.data snapshot) so a live WSS
      // delta that arrived between render and this click isn't dropped.
      swr.mutate(
        (current) => (current ?? []).map((n) => ({ ...n, is_read: true })),
        { revalidate: false }
      );

      await (
        await fetcher.postMarkAsRead()
      )();
    } catch (error) {
      const genericResponseAxiosError =
        error as AxiosError<GenericErrorResponse>;
      throw Error(
        genericResponseAxiosError.response?.data?.error ||
          "Error Posting Mark As Read"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, swr.mutate]);

  const markAsReadById = useCallback(
    async (id: string) => {
      if (!apiKey || !apiSecret) return;
      try {
        swr.mutate(
          (current) =>
            (current ?? []).map((n) =>
              n.id === id ? { ...n, is_read: true } : n
            ),
          { revalidate: false }
        );

        await axios.post(
          `${API_URL}/notifications/${id}/read`,
          {},
          { headers: authHeaders(apiKey, apiSecret) }
        );
      } catch (error) {
        // best-effort — local optimistic state still shows as read
        console.error("[useNotifications] markAsReadById failed", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiKey, apiSecret, swr.mutate]
  );

  const dismiss = useCallback(
    async (id: string) => {
      if (!apiKey || !apiSecret) return;
      try {
        swr.mutate((current) => (current ?? []).filter((n) => n.id !== id), {
          revalidate: false,
        });

        await axios.delete(`${API_URL}/notifications/${id}`, {
          headers: authHeaders(apiKey, apiSecret),
        });
      } catch (error) {
        console.error("[useNotifications] dismiss failed", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiKey, apiSecret, swr.mutate]
  );

  // Clear (hide) all notifications up to now. Distinct from markAsRead: this
  // empties the list (the backend advances a per-user cleared_at watermark and
  // filters them out) while keeping the rows in the DB. Notifications that
  // arrive afterwards still show.
  const clear = useCallback(async () => {
    if (!apiKey || !apiSecret) return;
    try {
      // Optimistic: empty the list immediately.
      swr.mutate([], { revalidate: false });

      await axios.post(
        `${API_URL}/notifications/clear`,
        {},
        { headers: authHeaders(apiKey, apiSecret) }
      );
    } catch (error) {
      // Recover the true state on failure (un-hide if the server rejected).
      swr.mutate();
      console.error("[useNotifications] clear failed", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, apiSecret, swr.mutate]);

  const unreadNotifications: GetNotifications200Response[] = useMemo(
    () => {
      let data: GetNotifications200Response[] = [];
      if (swr.data) data = swr.data as GetNotifications200Response[];
      return data.filter(
        (n: GetNotifications200Response) =>
          !n.is_read && isDisplayableNotification(n)
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [swr.data]
  );

  return {
    ...swr,
    markAsRead,
    markAsReadById,
    dismiss,
    clear,
    unreadNotifications,
  };
};
